/**
 * Type definitions mirroring the v1 manifest emitted by `j1628-pipeline`.
 *
 * The browser never produces these structures itself; they are built on
 * disk by the Python compiler so the names and units must stay in lockstep
 * with `notes/data-contract.md`.
 */

export type ProvenanceFlag = "observed" | "synthetic_table1" | "user_provided";

export type SpectralState = "Bright" | "Intermediate" | "Dim";

export interface LineMeasurement {
  fwhm_kms: number;
  fwhm_kms_err: number;
  ew_A: number;
  ew_A_err: number;
  flux_1e17: number;
  flux_1e17_err: number;
}

/**
 * Velocity-domain shape of a broad line relative to the systemic rest
 * frame (v1.2). Present only for objects whose source paper tabulates it.
 */
export interface LineKinematics {
  /** Offset of the line peak from rest, km/s; positive = redward. */
  peak_offset_kms: number;
  peak_offset_kms_err: number;
  centroid_kms: number | null;
  skew: number;
}

export type ObjectPhenomenon = "changing_look" | "binary_smbh_candidate";

export interface EpochRecord {
  index: number;
  date: string;
  mjd: number;
  telescope: string;
  instrument: string;
  aperture_arcsec: number;
  /** Null for objects with no discrete spectral states (v1.2). */
  state: SpectralState | null;
  kinematics?: Partial<Record<"Hbeta" | "Halpha", LineKinematics>> | null;
  provenance: ProvenanceFlag;
  source: string | null;
  /**
   * Native instrument metadata, surfaced from the v1.1 data package. Each
   * field is null when the source FITS does not encode it (and always
   * null for synthetic_table1 epochs because they have no instrument).
   */
  native_lambda_obs_min_A?: number | null;
  native_lambda_obs_max_A?: number | null;
  native_resolution_R?: number | null;
  measurements: {
    Halpha: LineMeasurement;
    Hbeta: LineMeasurement;
    log_l5100: { value: number; err: number };
    OIII_5007_flux_1e17: { value: number; err: number };
  };
}

export interface Manifest {
  schema_version?: string;
  object: {
    id: string;
    name: string;
    shorthand: string;
    z: number;
    galactic_av: number;
    phenomenon: ObjectPhenomenon;
    /** Citation for the source paper. */
    paper: string;
    /** Shown when epochs carry no discrete state (v1.2). */
    narrative: string | null;
    /** Per-state description shown for the current epoch (v1.3). */
    state_narrative: Record<SpectralState, string> | null;
    /** Object-specific reddening benchmarks from its paper, if any (v1.3). */
    reddening_benchmarks: string | null;
    /** Sentences appended to the dust and all-epochs mode paragraphs (v1.3). */
    reddening_note: string | null;
    all_epochs_note: string | null;
  };
  wavelength_rest: { start_A: number; step_A: number; n: number };
  spectra: {
    file: string;
    shape: [number, number];
    units: string;
    nan_means_no_coverage?: boolean;
  };
  uncertainty: {
    file: string;
    shape: [number, number];
    units: string;
    nan_means_no_coverage?: boolean;
  };
  extinction: {
    directory: string;
    laws: ExtinctionLawName[];
    files: Record<ExtinctionLawName, string>;
    description: string;
  };
  epochs: EpochRecord[];
  features: { Hgamma: number; Hbeta: number; OIII: number; Halpha: number } & Record<
    string,
    number
  >;
  /**
   * Per-object audio-strength denominators: the brightest epoch's line
   * fluxes, used to normalise sonified line strengths against this object's
   * own bright state. Optional so pre-audio_normalization packages still load.
   */
  audio_normalization?: { Halpha: number; Hbeta: number; OIII: number };
  build: {
    tool: string;
    version: string;
    timestamp: string;
    attempt_archive: boolean;
    inputs_dir?: string | null;
    provenance_counts?: Partial<Record<ProvenanceFlag, number>>;
  };
  scope: string;
}

export type ExtinctionLawName = "maiolino_agn" | "mw_rv44" | "lmc";

export interface SpectraPackage {
  manifest: Manifest;
  wavelength: Float32Array;
  flux: Float32Array;
  uncertainty: Float32Array;
  extinction: Record<ExtinctionLawName, Float32Array>;
}

export const STATE_COLORS: Record<SpectralState, string> = {
  Bright: "#60a5fa",
  Intermediate: "#34d399",
  Dim: "#f87171",
};

/** Colour for epochs with no spectral state. */
export const NEUTRAL_EPOCH_COLOR = "#c084fc";

export function stateColor(state: SpectralState | null | undefined): string {
  return state ? STATE_COLORS[state] : NEUTRAL_EPOCH_COLOR;
}

/** Peak offset of broad Hβ for an epoch, or null when not measured. */
export function hbetaOffsetKms(epoch: EpochRecord): number | null {
  return epoch.kinematics?.Hbeta?.peak_offset_kms ?? null;
}

export function formatOffsetKms(kms: number): string {
  const sign = kms > 0 ? "+" : kms < 0 ? "−" : "";
  return `${sign}${Math.round(Math.abs(kms))} km/s`;
}

/** Short epoch descriptor: the state when it has one, else the Hβ peak offset. */
export function epochDescriptor(epoch: EpochRecord): string {
  if (epoch.state) return epoch.state;
  const offset = hbetaOffsetKms(epoch);
  return offset === null ? "" : `Hβ ${formatOffsetKms(offset)}`;
}

export function epochLabel(epoch: EpochRecord): string {
  const descriptor = epochDescriptor(epoch);
  return descriptor ? `${epoch.date} · ${descriptor}` : epoch.date;
}
